import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, ExternalLink } from "lucide-react";
import { POST_STATUS, type PostStatus, type PostPatchInput } from "@avestate/shared";
import { usePost, usePatchPost } from "@/hooks/useBlog.js";
import { Button } from "@/components/ui/button.js";
import { Permalink } from "@/components/Permalink.js";
import { postUrl } from "@/lib/site.js";

const loc = (d: Record<string, string> | null | undefined) => (d && (d.it ?? Object.values(d)[0])) || "";

export default function BlogEditor(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  if (!id) return <p className="text-risk">id mancante</p>;

  const { data: post, isLoading } = usePost(id);
  const patch = usePatchPost(id);

  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("");
  const [cover, setCover] = useState("");
  const [tags, setTags] = useState("");
  const [status, setStatus] = useState<PostStatus>("bozza");

  const seededRef = useRef<string | null>(null);
  useEffect(() => {
    if (!post) return;
    // Seed once per post id — autosave invalidates ["post", id]; re-seeding on
    // every refetch would wipe text being typed while a save resolves.
    if (seededRef.current === id) return;
    seededRef.current = id;
    setTitle(loc(post.title));
    setExcerpt(loc(post.excerpt));
    setBody(loc(post.body));
    setCategory(post.category ?? "");
    setCover(post.cover_url ?? "");
    setTags((post.tags ?? []).join(", "));
    setStatus(post.status);
  }, [post, id]);

  if (isLoading) return <p className="font-mono text-meta uppercase text-ink-mute">carico…</p>;
  if (!post) return <p className="text-risk">Articolo non trovato</p>;

  function merge(field: "title" | "excerpt" | "body", value: string): Record<string, string> {
    const orig = (post?.[field] ?? {}) as Record<string, string>;
    return { ...orig, it: value };
  }

  async function save(extra?: Partial<PostPatchInput>) {
    const payload: PostPatchInput = {
      title: merge("title", title),
      excerpt: merge("excerpt", excerpt),
      body: merge("body", body),
      category: category || null,
      cover_url: cover || null,
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      ...extra,
    };
    try {
      await patch.mutateAsync(payload);
      toast.success(extra?.status ? `Stato → ${extra.status}` : "Articolo salvato");
      if (extra?.status) setStatus(extra.status);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  return (
    <div className="space-y-[22px]">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-meta uppercase tracking-wider text-ink-mute">
            <Link to="/blog" className="hover:text-ink">← Blog</Link>
          </div>
          <h1 className="mt-1 font-display text-[27px] font-bold tracking-tight text-ink">Articolo</h1>
          <Permalink url={postUrl(post.slug)} />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => void save()} disabled={patch.isPending}>Salva</Button>
          {status === "pubblicato"
            ? <Button variant="outline" onClick={() => void save({ status: "bozza" })} disabled={patch.isPending}>Riporta a bozza</Button>
            : <Button onClick={() => void save({ status: "pubblicato" })} disabled={patch.isPending}><Check className="size-4" /> Pubblica</Button>}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-[18px] lg:grid-cols-[1fr_300px]">
        <div className="ticks relative h-fit rounded-[5px] border border-rule bg-surface">
          <Field label="Titolo">
            <input className="ip" value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field label="Estratto">
            <textarea className="ip" rows={2} value={excerpt} onChange={(e) => setExcerpt(e.target.value)} />
          </Field>
          <Field label="Corpo (markdown)">
            <textarea className="ip font-mono text-[13px]" rows={16} value={body} onChange={(e) => setBody(e.target.value)} />
          </Field>
        </div>

        <div className="space-y-[18px]">
          <aside className="relative rounded-[5px] border border-rule bg-surface p-4">
            <h4 className="micro mb-3">Pubblicazione</h4>
            <div className="flex overflow-hidden rounded-[5px] border border-rule">
              {POST_STATUS.map((s) => (
                <button key={s} onClick={() => void save({ status: s })}
                  className={`flex-1 border-r border-rule px-1 py-2 font-mono text-[10.5px] uppercase tracking-wider last:border-r-0 ${status === s ? "bg-accent/10 text-accent" : "bg-surface-2 text-ink-mute"}`}>
                  {s}
                </button>
              ))}
            </div>
            {status === "pubblicato" ? (
              <a href={postUrl(post.slug)} target="_blank" rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-meta font-mono uppercase tracking-wider text-accent hover:underline">
                <ExternalLink className="size-3.5" /> vedi online
              </a>
            ) : null}
          </aside>
          <aside className="relative rounded-[5px] border border-rule bg-surface p-4 space-y-3">
            <h4 className="micro">Dettagli</h4>
            <LabeledInput label="Categoria" value={category} onChange={setCategory} placeholder="es. Mercato Garda" />
            <LabeledInput label="Cover URL" value={cover} onChange={setCover} placeholder="https://…" />
            <LabeledInput label="Tag (virgola)" value={tags} onChange={setTags} placeholder="garda, mercato" />
          </aside>
        </div>
      </div>

      <style>{`.ip{width:100%;background:var(--surface-2);border:1px solid var(--rule);border-radius:6px;color:var(--ink);font:inherit;padding:9px 12px;outline:none}.ip:focus{border-color:var(--accent)}`}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="border-b border-rule-soft p-[18px] last:border-b-0">
      <label className="micro mb-[9px] block">{label} <span className="text-ink-mute">· it</span></label>
      {children}
    </div>
  );
}
function LabeledInput({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }): JSX.Element {
  return (
    <div>
      <label className="micro mb-1.5 block">{label}</label>
      <input className="ip" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}
